import { Dispatch, Middleware } from 'redux';
import { all, call, put, select, spawn, takeLatest, takeLeading } from 'redux-saga/effects';

import { get$categories } from '@/api/categories';
import { getServicesByTorgRs, pService$get$s } from '@/api/services';
import { LanguageCode } from '@/i18n/languages';
import { pServiceFiltersActionTypes } from '@/store/polako-service-filters/polako-service-filters.actions';
import {
    pServiceKey,
    pServiceLoadCategoriesSuccess,
    PServicesActionTypes,
    pServicesLoad,
    pServicesLoadSuccess,
    PServicesQueryString,
    PServiceState,
} from '@/store/polako-services/polako-services.actions';
import { PServiceLoadArgs } from '@/store/polako-services/polako-services.types';
import { Category } from '@/types/category';
import { PServiceCardS, ServiceCardTorgRs } from '@/types/polako-services';
import { categoryIds, partnerServiceCategoryIds, storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';

import { selectFiltersAvailable, selectFiltersSelected } from '../polako-service-filters';

function* pServiceLoadCategories$() {
    const categories: Category[] = yield call(get$categories, categoryIds.service);
    if (!categories) return;

    yield put(pServiceLoadCategoriesSuccess(categories));
}

function* pServiceLoadCategoriesSaga() {
    yield takeLeading(PServicesActionTypes.pServiceLoadCategories, pServiceLoadCategories$);
}

function* pServiceLoad$(action: ReturnType<typeof pServicesLoad>) {
    if (!action.payload._queryString) return;

    try {
        const pServices: PServiceCardS[] = yield call(pService$get$s, action.payload._queryString);
        const torgRsServices: ServiceCardTorgRs[] = yield call(getServicesByTorgRs) || [];
        const transformed = transfromTorgRsToPservice(torgRsServices);
        const lang = Cookies.get(storageKeys.prefLanguage) as LanguageCode;
        const translated = translatePServices(pServices, lang);
        // @ts-ignore
        yield put(pServicesLoadSuccess(transformed.concat(translated)));
    } catch {}
}

function* PServicesLoadSaga() {
    yield takeLatest(PServicesActionTypes.pServicesLoad, pServiceLoad$);
}

function* PServicesLoadTrigger$() {
    const filtersAvailable: ReturnType<typeof selectFiltersAvailable> = yield select(selectFiltersAvailable);
    const filtersCount = Object.values(filtersAvailable).flat().length;
    if (filtersCount === 0 || (filtersAvailable.countryCode && filtersCount === 1)) return;

    const filtersSelected: ReturnType<typeof selectFiltersSelected> = yield select(selectFiltersSelected);

    const loadArgs: PServiceLoadArgs = {
        locationCity: filtersSelected.city.map((item) => item.value),
        typeLanguage: filtersSelected.language.map((item) => item.value),
        countryCode: filtersSelected.countryCode,
    } satisfies PServiceLoadArgs;
    yield put(pServicesLoad(loadArgs));
}

function* PServicesLoadTriggerSaga() {
    yield all([
        takeLatest(pServiceFiltersActionTypes.filtersLoadSuccess, PServicesLoadTrigger$),
        takeLatest(pServiceFiltersActionTypes.filterSelect, PServicesLoadTrigger$),
        takeLatest(pServiceFiltersActionTypes.filterDeselect, PServicesLoadTrigger$),
        takeLatest(pServiceFiltersActionTypes.filtersReset, PServicesLoadTrigger$),
        takeLatest(PServicesActionTypes.pServicesIncreaseCounter, PServicesLoadTrigger$),
    ]);
}

export function* pServiceEffects() {
    yield spawn(PServicesLoadSaga);
    yield spawn(PServicesLoadTriggerSaga);
    yield spawn(pServiceLoadCategoriesSaga);
}

export const pServiceQueryStringMw: Middleware<Dispatch, { [pServiceKey]: PServiceState }> =
    (api) => (next) => (action) => {
        if (action.type !== PServicesActionTypes.pServicesLoad) return next(action);
        const queryString = PServicesQueryString(action.payload);
        action.payload._queryString = queryString;
        if (queryString !== api.getState()[pServiceKey].queryString) {
            action.payload._queryString = queryString;
            return next(action);
        }
    };

function translatePServices(pServices: PServiceCardS[], lang: string) {
    return pServices.map((pService) => {
        const translation = pService.translations?.filter((trans) => trans.language === lang)[0]?.name;
        return translation ? { ...pService, name: translation } : pService;
    });
}

function transfromTorgRsToPservice(data: ServiceCardTorgRs[]): Partial<PServiceCardS>[] {
    const language = Cookies.get(storageKeys.prefLanguage) || 'en';
    // @ts-ignore
    return (
        data?.map((item, index) => ({
            id: item.title?.ru || index,
            name: language === 'sr' || language === 'en' ? item?.title['sr-Latn'] : item.title?.ru,
            status: 'active',
            gallery: [{ image_file: { id: item.image, filename_disk: item.image } }],
            rating: 0,
            animated: false,
            base_currency: 'RSD',
            price_type: 'regular',
            price: item.price.replace(' din', ' rsd'),
            slug: item.link,
            category: partnerServiceCategoryIds.torgRs,
            location: {
                name: item.city,
                address_link: '',
            },
        })) || []
    );
}
